/**
 * useAutonomySettings (J6, Wave 3) — fetch-on-mount + imperative tier/kill-switch
 * saves for the autonomy facade. Clones the launch-readiness patterns:
 * request-seq guard (useLaunchReadiness) + runAction (useSafeRollouts:
 * `await putSettings(); await refetch()` with a busy lock and an actionError
 * kept separate from the load error).
 *
 * AC-EH1 by construction: every action — success OR failure, including a
 * concurrent-write last-write-wins PUT — is followed by an explicit refetch,
 * so the facade always renders the SERVER state (no phantom tier).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { autonomyClient } from '../api/autonomyClient';
import type {
  AutonomyLever,
  AutonomySettingsData,
  AutonomySettingsUpdate,
  AutonomyTier,
} from '../types/autonomy.types';

/** The kill switch shares the busy lock with lever saves — one action at a time. */
export type AutonomyBusyKey = AutonomyLever | 'killSwitch';

export interface UseAutonomySettingsResult {
  data: AutonomySettingsData | null;
  /** True only for the initial load (no data yet). */
  loading: boolean;
  /** True while a refetch is in flight and stale data is still on screen. */
  refreshing: boolean;
  /** Load/refetch error — actions report through `actionError` instead. */
  error: Error | null;
  /** Lever (or 'killSwitch') with a save in flight, if any. */
  busyLever: AutonomyBusyKey | null;
  /** Error from the last save action (load errors go to `error`). */
  actionError: Error | null;
  refetch: () => Promise<void>;
  setLeverTier: (lever: AutonomyLever, tier: AutonomyTier) => Promise<void>;
  setKillSwitch: (engaged: boolean) => Promise<void>;
}

export function useAutonomySettings(projectId: string | undefined): UseAutonomySettingsResult {
  const [data, setData] = useState<AutonomySettingsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [busyLever, setBusyLever] = useState<AutonomyBusyKey | null>(null);
  const [actionError, setActionError] = useState<Error | null>(null);
  const requestSeq = useRef(0);

  const refetch = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    const seq = ++requestSeq.current;
    setRefreshing(true);
    setError(null);
    try {
      const fresh = await autonomyClient.getSettings(projectId);
      if (seq !== requestSeq.current) return; // stale response — a newer request superseded it
      setData(fresh);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [projectId]);

  useEffect(() => {
    requestSeq.current += 1; // invalidate in-flight requests from a previous project
    setData(null);
    setLoading(true);
    setError(null);
    setActionError(null);
    setBusyLever(null);
    void refetch();
  }, [refetch]);

  const runAction = useCallback(
    async (busyKey: AutonomyBusyKey, update: AutonomySettingsUpdate) => {
      if (!projectId || busyLever) return;
      setBusyLever(busyKey);
      setActionError(null);
      try {
        await autonomyClient.putSettings(projectId, update);
      } catch (err) {
        setActionError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setBusyLever(null);
      }
      // Explicit refresh even after a failed action — the server state (incl.
      // a concurrent writer's last-write-wins result) is the only truth.
      await refetch();
    },
    [projectId, busyLever, refetch],
  );

  const setLeverTier = useCallback(
    (lever: AutonomyLever, tier: AutonomyTier) => runAction(lever, { levers: { [lever]: tier } }),
    [runAction],
  );

  const setKillSwitch = useCallback(
    (engaged: boolean) => runAction('killSwitch', { killSwitch: engaged }),
    [runAction],
  );

  return {
    data,
    loading,
    refreshing,
    error,
    busyLever,
    actionError,
    refetch,
    setLeverTier,
    setKillSwitch,
  };
}
